<template>
  <div class="automation-editor" ref="editorContainer">
    <!-- Header -->
    <div class="editor-header">
      <div class="header-left">
        <n-button quaternary @click="goBack">
          <template #icon><n-icon><ArrowBackIcon /></n-icon></template>
        </n-button>
        <h2>{{ automation?.name || 'Untitled Automation' }}</h2>
        <n-tag :type="getStatusType(automation?.status)">
          {{ automation?.status || 'draft' }}
        </n-tag>
      </div>
      <div class="header-right">
        <n-button-group>
          <n-button @click="testAutomation" :loading="testing">
            <template #icon><n-icon><PlayIcon /></n-icon></template>
            Test
          </n-button>
          <n-button type="primary" @click="saveAutomation" :loading="saving">
            <template #icon><n-icon><SaveIcon /></n-icon></template>
            Save
          </n-button>
        </n-button-group>
        <n-dropdown :options="moreOptions" @select="handleMoreAction">
          <n-button>
            <template #icon><n-icon><MoreIcon /></n-icon></template>
          </n-button>
        </n-dropdown>
      </div>
    </div>

    <!-- Main Editor Area -->
    <div class="editor-main">
      <!-- Node Palette (Left Sidebar) -->
      <div class="node-palette">
        <n-input
          v-model:value="searchNodes"
          placeholder="Search nodes..."
          clearable
          class="palette-search"
        />

        <n-collapse :default-expanded-names="['triggers', 'actions']">
          <n-collapse-item title="Triggers" name="triggers">
            <div class="node-list">
              <div
                v-for="node in filteredNodes('trigger')"
                :key="node.type"
                class="palette-node"
                draggable="true"
                @dragstart="onDragStart($event, node)"
              >
                <n-icon :component="getNodeIcon(node.icon)" />
                <span>{{ node.name }}</span>
              </div>
            </div>
          </n-collapse-item>

          <n-collapse-item title="Actions" name="actions">
            <div class="node-list">
              <div
                v-for="node in filteredNodes('action')"
                :key="node.type"
                class="palette-node"
                draggable="true"
                @dragstart="onDragStart($event, node)"
              >
                <n-icon :component="getNodeIcon(node.icon)" />
                <span>{{ node.name }}</span>
              </div>
            </div>
          </n-collapse-item>

          <n-collapse-item title="Logic" name="logic">
            <div class="node-list">
              <div
                v-for="node in filteredNodes('logic')"
                :key="node.type"
                class="palette-node"
                draggable="true"
                @dragstart="onDragStart($event, node)"
              >
                <n-icon :component="getNodeIcon(node.icon)" />
                <span>{{ node.name }}</span>
              </div>
            </div>
          </n-collapse-item>
        </n-collapse>
      </div>

      <!-- Canvas Area -->
      <div
        class="canvas-area"
        ref="canvasRef"
        @drop="onDrop"
        @dragover.prevent
        @click="deselectNode"
      >
        <svg class="connections-layer" :viewBox="`0 0 ${canvasWidth} ${canvasHeight}`">
          <g v-for="conn in connections" :key="conn.id">
            <path
              :d="getConnectionPath(conn)"
              class="connection-line"
              :class="{ selected: conn.id === selectedConnection }"
              @click.stop="selectConnection(conn.id)"
            />
          </g>
          <!-- Connection being drawn -->
          <path
            v-if="drawingConnection"
            :d="drawingConnectionPath"
            class="connection-line drawing"
          />
        </svg>

        <!-- Nodes -->
        <div
          v-for="node in nodes"
          :key="node.id"
          class="canvas-node"
          :class="{ selected: node.id === selectedNode }"
          :style="{ left: node.position.x + 'px', top: node.position.y + 'px' }"
          @mousedown.stop="startDragNode($event, node)"
          @click.stop="selectNode(node)"
        >
          <div class="node-header" :style="{ backgroundColor: getNodeColor(node.type) }">
            <n-icon :component="getNodeIcon(nodeDefinitions[node.type]?.icon)" size="16" />
            <span>{{ node.name || nodeDefinitions[node.type]?.name }}</span>
          </div>
          <div class="node-body">
            <div class="node-summary">
              {{ getNodeSummary(node) }}
            </div>
          </div>
          <!-- Input ports -->
          <div
            v-for="(input, idx) in getNodeInputs(node)"
            :key="'in-' + idx"
            class="port input-port"
            :style="{ top: (30 + idx * 20) + 'px' }"
            @mouseup="endConnection(node.id, input.name)"
          />
          <!-- Output ports -->
          <div
            v-for="(output, idx) in getNodeOutputs(node)"
            :key="'out-' + idx"
            class="port output-port"
            :style="{ top: (30 + idx * 20) + 'px' }"
            @mousedown.stop="startConnection($event, node.id, output.name)"
          >
            <span class="port-label">{{ output.label }}</span>
          </div>
        </div>

        <!-- Empty state -->
        <div v-if="nodes.length === 0" class="empty-canvas">
          <n-icon size="64"><AddCircleIcon /></n-icon>
          <p>Drag nodes from the palette to build your automation</p>
        </div>
      </div>

      <!-- Node Config Panel (Right Sidebar) -->
      <div class="config-panel" v-if="selectedNode">
        <div class="config-header">
          <h3>{{ getSelectedNodeDef?.name }}</h3>
          <n-button text @click="deleteSelectedNode">
            <template #icon><n-icon><TrashIcon /></n-icon></template>
          </n-button>
        </div>

        <n-form :model="selectedNodeConfig" class="config-form">
          <n-form-item label="Node Name">
            <n-input v-model:value="selectedNodeData.name" placeholder="Optional custom name" />
          </n-form-item>

          <!-- Dynamic config fields based on node type -->
          <component
            :is="getConfigComponent(selectedNodeData.type)"
            v-model="selectedNodeConfig"
            :node="selectedNodeData"
          />
        </n-form>
      </div>
    </div>

    <!-- Test Results Modal -->
    <n-modal v-model:show="showTestResults" preset="card" title="Test Results" style="width: 600px">
      <n-timeline>
        <n-timeline-item
          v-for="step in testResults"
          :key="step.node_id"
          :type="step.status === 'completed' ? 'success' : step.status === 'failed' ? 'error' : 'info'"
          :title="step.node_name"
        >
          <n-collapse>
            <n-collapse-item title="Details">
              <pre>{{ JSON.stringify(step.output_data, null, 2) }}</pre>
            </n-collapse-item>
          </n-collapse>
        </n-timeline-item>
      </n-timeline>
    </n-modal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  NButton, NButtonGroup, NIcon, NTag, NInput, NCollapse, NCollapseItem,
  NForm, NFormItem, NModal, NTimeline, NTimelineItem, NDropdown
} from 'naive-ui'
import { useToast } from '@shared/composables/useToast.js'
import {
  ArrowBack as ArrowBackIcon,
  Play as PlayIcon,
  Save as SaveIcon,
  EllipsisVertical as MoreIcon,
  AddCircle as AddCircleIcon,
  Trash as TrashIcon,
  Flash as BoltIcon,
  Time as ClockIcon,
  Globe as HttpIcon,
  GitBranch as BranchIcon,
  ChatbubbleEllipses as WhatsAppIcon,
  Settings as SettingsIcon
} from '@vicons/ionicons5'
import { useAdmin } from '@/composables/useAdmin'

// Node config components (would be separate files in production)
import GenericNodeConfig from '@/components/automation/GenericNodeConfig.vue'

const route = useRoute()
const router = useRouter()
const toast = useToast()
const { api } = useAdmin()

// State
const automation = ref(null)
const nodes = ref([])
const connections = ref([])
const nodeDefinitions = ref({})
const loading = ref(false)
const saving = ref(false)
const testing = ref(false)
const showTestResults = ref(false)
const testResults = ref([])

// Canvas state
const canvasRef = ref(null)
const canvasWidth = ref(2000)
const canvasHeight = ref(2000)
const selectedNode = ref(null)
const selectedConnection = ref(null)
const searchNodes = ref('')

// Drag state
const dragNode = ref(null)
const dragOffset = ref({ x: 0, y: 0 })

// Connection drawing state
const drawingConnection = ref(false)
const connectionStart = ref(null)
const connectionEnd = ref({ x: 0, y: 0 })

// Computed
const selectedNodeData = computed(() =>
  nodes.value.find(n => n.id === selectedNode.value)
)

const selectedNodeConfig = computed({
  get: () => selectedNodeData.value?.config || {},
  set: (val) => {
    if (selectedNodeData.value) {
      selectedNodeData.value.config = val
    }
  }
})

const getSelectedNodeDef = computed(() =>
  nodeDefinitions.value[selectedNodeData.value?.type]
)

const drawingConnectionPath = computed(() => {
  if (!connectionStart.value) return ''
  const startNode = nodes.value.find(n => n.id === connectionStart.value.nodeId)
  if (!startNode) return ''

  const startX = startNode.position.x + 200 // Node width
  const startY = startNode.position.y + 40
  const endX = connectionEnd.value.x
  const endY = connectionEnd.value.y

  return `M ${startX} ${startY} C ${startX + 50} ${startY}, ${endX - 50} ${endY}, ${endX} ${endY}`
})

const moreOptions = [
  { label: 'Settings', key: 'settings' },
  { label: 'Duplicate', key: 'duplicate' },
  { label: 'Export', key: 'export' },
  { type: 'divider' },
  { label: 'Delete', key: 'delete' }
]

// Icon mapping
const iconMap = {
  'bolt': BoltIcon,
  'clock': ClockIcon,
  'http': HttpIcon,
  'git-branch': BranchIcon,
  'whatsapp': WhatsAppIcon,
  'default': SettingsIcon
}

// Methods
function goBack() {
  router.push('/automations')
}

function getStatusType(status) {
  const types = { draft: 'default', active: 'success', paused: 'warning', archived: 'error' }
  return types[status] || 'default'
}

function getNodeIcon(icon) {
  return iconMap[icon] || iconMap.default
}

function getNodeColor(type) {
  const def = nodeDefinitions.value[type]
  const colors = {
    trigger: '#10b981',
    action: '#3b82f6',
    logic: '#8b5cf6'
  }
  return colors[def?.category] || '#6b7280'
}

function filteredNodes(category) {
  const defs = Object.values(nodeDefinitions.value)
  return defs.filter(n => {
    if (n.category !== category) return false
    if (searchNodes.value) {
      const search = searchNodes.value.toLowerCase()
      return n.name.toLowerCase().includes(search) || n.type.toLowerCase().includes(search)
    }
    return true
  })
}

function getNodeInputs(node) {
  return nodeDefinitions.value[node.type]?.inputs || []
}

function getNodeOutputs(node) {
  return nodeDefinitions.value[node.type]?.outputs || []
}

function getNodeSummary(node) {
  // Generate a short summary based on config
  const config = node.config || {}
  if (node.type === 'whatsapp_send') {
    return config.to ? `To: ${config.to}` : 'Configure recipient'
  }
  if (node.type === 'http_request') {
    return config.url ? `${config.method || 'GET'} ${config.url}` : 'Configure request'
  }
  if (node.type === 'if_else') {
    return 'Branch on condition'
  }
  return ''
}

function getConnectionPath(conn) {
  const fromNode = nodes.value.find(n => n.id === conn.from)
  const toNode = nodes.value.find(n => n.id === conn.to)
  if (!fromNode || !toNode) return ''

  const startX = fromNode.position.x + 200
  const startY = fromNode.position.y + 40
  const endX = toNode.position.x
  const endY = toNode.position.y + 40

  // Bezier curve
  const midX = (startX + endX) / 2
  return `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`
}

function getConfigComponent(type) {
  // In production, map to specific config components
  return GenericNodeConfig
}

// Drag & Drop
function onDragStart(event, nodeDef) {
  event.dataTransfer.setData('node-type', nodeDef.type)
}

function onDrop(event) {
  const nodeType = event.dataTransfer.getData('node-type')
  if (!nodeType) return

  const rect = canvasRef.value.getBoundingClientRect()
  const x = event.clientX - rect.left
  const y = event.clientY - rect.top

  addNode(nodeType, { x, y })
}

function addNode(type, position) {
  const def = nodeDefinitions.value[type]
  if (!def) return

  const node = {
    id: `node_${Date.now()}`,
    type,
    name: def.name,
    config: {},
    position
  }

  nodes.value.push(node)
  selectNode(node)
}

function startDragNode(event, node) {
  dragNode.value = node
  dragOffset.value = {
    x: event.clientX - node.position.x,
    y: event.clientY - node.position.y
  }

  const onMouseMove = (e) => {
    node.position.x = e.clientX - dragOffset.value.x
    node.position.y = e.clientY - dragOffset.value.y
  }

  const onMouseUp = () => {
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
    dragNode.value = null
  }

  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)
}

// Node selection
function selectNode(node) {
  selectedNode.value = node.id
  selectedConnection.value = null
}

function deselectNode() {
  selectedNode.value = null
  selectedConnection.value = null
}

function deleteSelectedNode() {
  if (!selectedNode.value) return

  nodes.value = nodes.value.filter(n => n.id !== selectedNode.value)
  connections.value = connections.value.filter(
    c => c.from !== selectedNode.value && c.to !== selectedNode.value
  )
  selectedNode.value = null
}

// Connections
function startConnection(event, nodeId, port) {
  drawingConnection.value = true
  connectionStart.value = { nodeId, port }
  connectionEnd.value = { x: event.clientX, y: event.clientY }

  const onMouseMove = (e) => {
    const rect = canvasRef.value.getBoundingClientRect()
    connectionEnd.value = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    }
  }

  const onMouseUp = () => {
    drawingConnection.value = false
    connectionStart.value = null
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
  }

  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)
}

function endConnection(nodeId, port) {
  if (!connectionStart.value) return
  if (connectionStart.value.nodeId === nodeId) return

  connections.value.push({
    id: `conn_${Date.now()}`,
    from: connectionStart.value.nodeId,
    fromPort: connectionStart.value.port,
    to: nodeId,
    toPort: port
  })
}

function selectConnection(id) {
  selectedConnection.value = id
  selectedNode.value = null
}

// API
async function fetchAutomation() {
  loading.value = true
  try {
    const [automationRes, nodesRes] = await Promise.all([
      api.get(`/automation/automations/${route.params.id}`),
      api.get('/automation/nodes')
    ])

    automation.value = automationRes.data

    // Parse definition
    const definition = automation.value.definition || { nodes: {}, connections: [] }
    nodes.value = Object.entries(definition.nodes || {}).map(([id, node]) => ({
      id,
      ...node
    }))
    connections.value = definition.connections || []

    // Build node definitions lookup
    nodeDefinitions.value = {}
    for (const node of nodesRes.data.nodes || []) {
      nodeDefinitions.value[node.type] = node
    }
  } catch (error) {
    toast.error('Failed to load automation')
  } finally {
    loading.value = false
  }
}

async function saveAutomation() {
  saving.value = true
  try {
    // Build definition from nodes and connections
    const definition = {
      nodes: {},
      connections: connections.value
    }

    for (const node of nodes.value) {
      definition.nodes[node.id] = {
        type: node.type,
        name: node.name,
        config: node.config,
        position: node.position
      }
    }

    await api.put(`/automation/automations/${route.params.id}`, {
      ...automation.value,
      definition
    })

    toast.success('Automation saved')
  } catch (error) {
    toast.error('Failed to save automation')
  } finally {
    saving.value = false
  }
}

async function testAutomation() {
  testing.value = true
  try {
    const response = await api.post(`/automation/automations/${route.params.id}/execute`, {
      test_mode: true
    })

    testResults.value = response.data.node_executions || []
    showTestResults.value = true
  } catch (error) {
    toast.error('Test failed: ' + (error.response?.data?.detail || error.message))
  } finally {
    testing.value = false
  }
}

async function handleMoreAction(key) {
  switch (key) {
    case 'settings':
      // Open settings modal
      break
    case 'duplicate':
      await api.post(`/automation/automations/${route.params.id}/duplicate`)
      toast.success('Automation duplicated')
      router.push('/automations')
      break
    case 'export':
      const blob = new Blob([JSON.stringify(automation.value, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${automation.value.name}.json`
      a.click()
      break
    case 'delete':
      await api.delete(`/automation/automations/${route.params.id}`)
      toast.success('Automation deleted')
      router.push('/automations')
      break
  }
}

onMounted(fetchAutomation)
</script>

<style scoped>
.automation-editor {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--body-color);
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: var(--card-color);
  border-bottom: 1px solid var(--border-color);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-left h2 {
  margin: 0;
  font-size: 18px;
}

.header-right {
  display: flex;
  gap: 8px;
}

.editor-main {
  flex: 1;
  display: flex;
  overflow: hidden;
}

/* Node Palette */
.node-palette {
  width: 240px;
  background: var(--card-color);
  border-right: 1px solid var(--border-color);
  overflow-y: auto;
}

.palette-search {
  margin: 12px;
  width: calc(100% - 24px);
}

.node-list {
  padding: 0 12px;
}

.palette-node {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 4px;
  border-radius: 6px;
  cursor: grab;
  background: var(--body-color);
  transition: background 0.2s;
}

.palette-node:hover {
  background: var(--hover-color);
}

/* Canvas */
.canvas-area {
  flex: 1;
  position: relative;
  overflow: auto;
  background:
    linear-gradient(90deg, var(--border-color) 1px, transparent 1px),
    linear-gradient(var(--border-color) 1px, transparent 1px);
  background-size: 20px 20px;
}

.connections-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.connection-line {
  fill: none;
  stroke: var(--primary-color);
  stroke-width: 2;
  pointer-events: stroke;
}

.connection-line.selected {
  stroke-width: 3;
  stroke: var(--warning-color);
}

.connection-line.drawing {
  stroke-dasharray: 5, 5;
}

/* Canvas Nodes */
.canvas-node {
  position: absolute;
  width: 200px;
  background: var(--card-color);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  cursor: move;
  border: 2px solid transparent;
}

.canvas-node.selected {
  border-color: var(--primary-color);
}

.node-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px 6px 0 0;
  color: white;
  font-weight: 500;
  font-size: 13px;
}

.node-body {
  padding: 12px;
}

.node-summary {
  font-size: 12px;
  color: var(--text-color-3);
}

/* Ports */
.port {
  position: absolute;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--primary-color);
  border: 2px solid var(--card-color);
  cursor: crosshair;
}

.input-port {
  left: -6px;
}

.output-port {
  right: -6px;
}

.port-label {
  position: absolute;
  right: 16px;
  font-size: 10px;
  color: var(--text-color-3);
  white-space: nowrap;
}

/* Empty state */
.empty-canvas {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: var(--text-color-3);
}

/* Config Panel */
.config-panel {
  width: 300px;
  background: var(--card-color);
  border-left: 1px solid var(--border-color);
  padding: 16px;
  overflow-y: auto;
}

.config-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.config-header h3 {
  margin: 0;
}

.config-form {
  margin-top: 12px;
}
</style>
